import { Job, JobType, TaskType } from '../../../../../clients/quipli-service/job-tasks/job-tasks';
import { OrderMedia, OrderProduct } from '../../../../../clients/quipli-service/order/order';
import { ProductInventoryUnit } from '../../../../../clients/quipli-service/product/product';
import { Collapsible } from '../../../../../components/collapsible/collapsible';
import { Button } from '../../../../../components/form/button';
import { InspectionUnitHeader } from '../inspection-header';
import { ChecklistSection } from './sections/checklist-section';
import { HoursFuelSection } from './sections/hours-fuel-section';
import { InspectionStatus } from './inspection-status';
import { areUnitTasksComplete } from './utils';
import { Dropdown } from '../../../../../components/dropdown/dropdown';
import Icon from '../../../../../components/icons/icon';
import { Typography } from '../../../../../components/typography/typography';
import { useJobTaskApi } from '../../../../../clients/quipli-service/job-tasks/job-task-api';
import FileSaver from 'file-saver';
import { toast } from '../../../../../util/toasts';
import { PdfPrinter } from '../../../../../components/pdf-printer/pdf-printer';
import { useAdmin } from '../../../../../hooks/use-admin-context';
import { useState } from 'react';

/**
 * Unit inspection item, displays a single unit's inspection status and tasks.
 * @param inspection - The inspection.
 * @param orderProduct - The order product.
 * @param unit - The unit.
 * @param unitTrackingVia - The unit tracking via.
 * @param fuelUnit - The fuel unit.
 */
export const UnitInspectionView = ({
  inspection,
  orderProduct,
  unit,
  unitTrackingVia,
  fuelUnit,
  fetch,
  setSelectedUnit,
  jobType,
  mediaList,
}: {
  inspection: Job;
  orderProduct: OrderProduct;
  unit: ProductInventoryUnit;
  unitTrackingVia: 'serialNumber' | 'unitNumber';
  fuelUnit: 'gallons' | 'liters';
  fetch: () => void;
  setSelectedUnit: (unit: ProductInventoryUnit) => void;
  jobType: JobType;
  mediaList: OrderMedia[];
}) => {
  const hoursTask = inspection.tasks.find(
    (task) => task.metadata?.productUnitId === unit.id && task.type === TaskType.MachineHours,
  );
  const checklistTask = inspection.tasks.find(
    (task) => task.metadata?.productUnitId === unit.id && task.type === TaskType.InspectionChecklist,
  );
  return (
    <div key={unit.id} className="inspection-unit bg-grey-50 rounded-md p-2">
      <Collapsible
        trigger={() => (
          <div className="flex justify-between items-center">
            <InspectionUnitHeader product={orderProduct.product} unitNumber={unit[unitTrackingVia]} />

            <div className="flex flex-row gap-2">
              {areUnitTasksComplete(inspection.tasks, unit.id) ? (
                <InspectionStatus inspection={inspection} unit={unit} />
              ) : (
                <Button className="button button-yellow" onClick={() => setSelectedUnit(unit)}>
                  {checklistTask?.startedAt ? 'Resume' : 'Start'}
                </Button>
              )}
              <UnitInspectionDropdown
                jobId={inspection.id}
                unitId={unit.id}
                isJobComplete={areUnitTasksComplete(inspection.tasks, unit.id)}
              />
            </div>
          </div>
        )}
        openAtStart={false}
      >
        <div>
          {hoursTask && (
            <HoursFuelSection hoursTask={hoursTask} jobType={jobType} orderProduct={orderProduct} fuelUnit={fuelUnit} />
          )}
          {checklistTask && <ChecklistSection checklistTask={checklistTask} mediaList={mediaList} fetch={fetch} />}
        </div>
      </Collapsible>
    </div>
  );
};

const UnitInspectionDropdown = ({
  jobId,
  unitId,
  isJobComplete,
}: {
  jobId: string;
  unitId: number;
  isJobComplete: boolean;
}) => {
  const { getReport } = useJobTaskApi();
  const { contextPath } = useAdmin();
  const [pdfToPrint, setPdfToPrint] = useState<string | undefined>();

  async function downloadInspection() {
    try {
      const file = await getReport(jobId, unitId);
      const fileType = 'application/pdf';

      const data = new Blob([file], { type: fileType });

      FileSaver.saveAs(data, `Inspection.pdf`);
    } catch (error: any) {
      toast(error.message || 'Failed to generate inspection', 'error');
    }
  }

  const verifyCompleteFirst = (op: () => void) => {
    if (isJobComplete) {
      op();
    } else {
      toast('Inspection must be complete before generating a report', 'warning');
    }
  };

  return (
    <div>
      <Dropdown
        trigger={() => (
          <Button className="button-tertiary">
            <Icon icon="DotsHorizontal" />
          </Button>
        )}
      >
        <div onClick={() => verifyCompleteFirst(downloadInspection)} className="flex flex-row gap-2">
          <Icon icon="Download" stroke="var(--quipli-black-800)" />
          <Typography type="text-sm" className="text-quipli-black-800">
            Download Report
          </Typography>
        </div>

        <div
          onClick={() =>
            verifyCompleteFirst(() =>
              setPdfToPrint(`${contextPath}quipli-service/v1/inspections/jobs/${jobId}/report/${unitId}`),
            )
          }
          className="flex flex-row gap-2"
        >
          <Icon icon="Printer" color="var(--quipli-black-800)" />
          <Typography type="text-sm" className="text-quipli-black-800">
            Print Report
          </Typography>
        </div>

        {/* <div onClick={() => noop()} className="flex flex-row gap-2">
          <Icon icon="Envelope" color="var(--quipli-black-800)" />
          <Typography type="text-sm" className="text-quipli-black-800">
            Send Report
          </Typography>
        </div> */}
      </Dropdown>
      <PdfPrinter pdf={pdfToPrint} onComplete={() => setPdfToPrint(undefined)} />
    </div>
  );
};
